import * as Sentry from '@sentry/react'

// Error monitoring. Only initialized when VITE_SENTRY_DSN is present so
// dev runs without it stay quiet (and so a dev forgetting to set the env
// var doesn't get a noisy "DSN missing" warning on every page load).
//
// Scope deliberately tight: error reporting only. No tracing, no session
// replay, no metrics — those eat the free tier quota fast and aren't
// needed for the launch. Add later if we hit a problem the bare error
// reports don't help with.

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    if (import.meta.env.PROD) {
      console.warn('[sentry] VITE_SENTRY_DSN not set — errors won\'t be reported')
    }
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Sample 100% of errors. With ~5k/mo quota and a small user base
    // (touring crews, low traffic), capturing every error is fine.
    sampleRate: 1.0,
    // Don't capture noisy expected errors — Supabase RLS rejections,
    // user-cancelled fetches, third-party widget errors.
    ignoreErrors: [
      // Browser extensions, ad blockers
      'Non-Error promise rejection captured',
      'ResizeObserver loop',
      // User abandoning a page mid-fetch
      'The user aborted a request',
      'AbortError',
      // Network blips during PWA reload
      'Failed to fetch',
    ],
    // Strip query params from URLs in breadcrumbs — Supabase URLs include
    // tokens we don't want in our error logs.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        if (typeof breadcrumb.data?.url === 'string') {
          breadcrumb.data.url = breadcrumb.data.url.split('?')[0]
        }
      }
      return breadcrumb
    },
  })
}

// Re-export so callers can do `import { captureException } from '@/lib/sentry'`
// without pulling in the full @sentry/react surface or remembering whether
// init was called.
export const captureException = (err: unknown, context?: Record<string, unknown>) => {
  Sentry.captureException(err, context ? { extra: context } : undefined)
}

export const setUser = (user: { id: string; email?: string } | null) => {
  Sentry.setUser(user)
}
